import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ShieldCheck, Truck, CreditCard, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
type Bundle = {
  id: string;
  bottles: number;
  title: string;
  subtitle: string;
  price: number;
  originalPrice: number;
  savings: number;
  badge?: string;
  popular?: boolean;
};
const bundles: Bundle[] = [{
  id: '5-bottle',
  bottles: 5,
  title: 'Buy 3 Get 2 Free',
  subtitle: '5x GINEXT Ginger Oil',
  price: 89.97,
  originalPrice: 149.95,
  savings: 60,
  badge: 'BEST VALUE',
  popular: true
}, {
  id: '3-bottle',
  bottles: 3,
  title: 'Buy 2 Get 1 Free',
  subtitle: '3x GINEXT Ginger Oil',
  price: 59.98,
  originalPrice: 89.97,
  savings: 33,
  badge: 'MOST POPULAR'
}, {
  id: '1-bottle',
  bottles: 1,
  title: 'Single Bottle',
  subtitle: '1x GINEXT Ginger Oil',
  price: 29.99,
  originalPrice: 29.99,
  savings: 0
}];
export function CheckoutPage() {
  const [selectedBundle, setSelectedBundle] = useState<Bundle>(bundles[0]);
  const freeShipping = selectedBundle.price >= 50;
  const {
    trackPageView,
    trackConversion
  } = useAnalytics();
  useEffect(() => {
    // Track page view on mount
    trackPageView('Checkout Page');
  }, []);
  const handlePurchase = () => {
    trackConversion(selectedBundle.price);
    // In a real app, this would proceed to payment processing
    alert('Thank you for your purchase! This is a demo.');
  };
  return <div className="min-h-screen bg-bone" data-id="element-1416">
      {/* Header */}
      <header className="bg-white border-b border-bone-dark sticky top-0 z-50" data-id="element-1417">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between" data-id="element-1418">
          <Link to="/home" className="flex items-center gap-2 text-charcoal hover:text-ginger transition-colors" data-id="element-1419">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1420" />
            <span className="font-semibold text-sm sm:text-base" data-id="element-1421">Back</span>
          </Link>
          <div className="text-xl sm:text-2xl font-bold text-charcoal" data-id="element-1422">
            GINEXT
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-12" data-id="element-1423">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12" data-id="element-1424">
          {/* Checkout Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-1425">
            <h1 className="text-2xl sm:text-3xl font-bold text-charcoal mb-6 sm:mb-8" data-id="element-1426">
              Checkout
            </h1>

            {/* Bundle Selection */}
            <div className="mb-6 sm:mb-8" data-id="element-1427">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4" data-id="element-1428">
                Select Your Bundle
              </h2>
              <div className="space-y-3" data-id="element-1429">
                {bundles.map((bundle, index) => <motion.div key={bundle.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                delay: index * 0.05
              }} onClick={() => setSelectedBundle(bundle)} className={`relative bg-white rounded-xl p-3 sm:p-5 border-2 cursor-pointer transition-all ${selectedBundle.id === bundle.id ? 'border-ginger shadow-warm' : 'border-bone-dark hover:border-ginger/50'}`} data-id="element-1430">
                    {/* Badges Container */}
                    <div className="flex flex-wrap gap-1 mb-2 sm:mb-0" data-id="element-1431">
                      {bundle.badge && <span className="bg-herbal text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full" data-id="element-1432">
                          {bundle.badge}
                        </span>}
                      {bundle.price >= 50 && <span className="bg-charcoal text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex items-center gap-1" data-id="element-1433">
                          <Truck className="w-2.5 h-2.5 sm:w-3 sm:h-3" data-id="element-1434" />
                          FREE SHIPPING
                        </span>}
                    </div>

                    <div className="flex items-start gap-2 sm:gap-4" data-id="element-1435">
                      {/* Radio Button */}
                      <div className="mt-1 flex-shrink-0" data-id="element-1436">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedBundle.id === bundle.id ? 'border-ginger bg-ginger' : 'border-bone-dark'}`} data-id="element-1437">
                          {selectedBundle.id === bundle.id && <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" data-id="element-1438" />}
                        </div>
                      </div>

                      {/* Product Image */}
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-bone rounded-lg flex-shrink-0 overflow-hidden" data-id="element-1439">
                        <img src="https://cdn.magicpatterns.com/uploads/eTQ9DmqViHRFHehcQ8Qywk/GINEXT.png" alt="GINEXT Ginger Oil" className="w-full h-full object-cover" loading="lazy" data-id="element-1440" />
                      </div>

                      {/* Bundle Info */}
                      <div className="flex-1 min-w-0" data-id="element-1441">
                        <h3 className="font-bold text-charcoal text-sm sm:text-lg mb-0.5 sm:mb-1" data-id="element-1442">
                          {bundle.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-charcoal-light mb-2" data-id="element-1443">
                          {bundle.subtitle}
                        </p>

                        <div className="flex flex-wrap items-baseline gap-1 sm:gap-2 mb-1 sm:mb-2" data-id="element-1444">
                          <span className="text-xl sm:text-2xl font-bold text-ginger" data-id="element-1445">
                            ${bundle.price.toFixed(2)}
                          </span>
                          {bundle.savings > 0 && <>
                              <span className="text-xs sm:text-sm text-charcoal-light line-through" data-id="element-1446">
                                ${bundle.originalPrice.toFixed(2)}
                              </span>
                              <span className="text-xs sm:text-sm font-semibold text-herbal" data-id="element-1447">
                                Save {bundle.savings}%
                              </span>
                            </>}
                        </div>

                        <p className="text-[10px] sm:text-xs text-charcoal-light" data-id="element-1448">
                          ${(bundle.price / bundle.bottles).toFixed(2)} per
                          bottle
                          {bundle.price < 50 && ' + shipping'}
                        </p>
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-bone-dark mb-4 sm:mb-6" data-id="element-1449">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4" data-id="element-1450">
                Contact Information
              </h2>
              <div data-id="element-1451">
                <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1452">
                  Email
                </label>
                <input type="email" placeholder="you@example.com" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1453" />
              </div>
            </div>

            {/* Shipping Information */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-bone-dark mb-4 sm:mb-6" data-id="element-1454">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4" data-id="element-1455">
                Shipping Address
              </h2>
              <div className="space-y-4" data-id="element-1456">
                <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-1457">
                  <div data-id="element-1458">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1459">
                      First Name
                    </label>
                    <input type="text" placeholder="John" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1460" />
                  </div>
                  <div data-id="element-1461">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1462">
                      Last Name
                    </label>
                    <input type="text" placeholder="Doe" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1463" />
                  </div>
                </div>
                <div data-id="element-1464">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1465">
                    Address
                  </label>
                  <input type="text" placeholder="123 Main St" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1466" />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-1467">
                  <div data-id="element-1468">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1469">
                      City
                    </label>
                    <input type="text" placeholder="New York" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1470" />
                  </div>
                  <div data-id="element-1471">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1472">
                      ZIP Code
                    </label>
                    <input type="text" placeholder="10001" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1473" />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-bone-dark" data-id="element-1474">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4" data-id="element-1475">
                Payment Information
              </h2>
              <div className="space-y-4" data-id="element-1476">
                <div data-id="element-1477">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1478">
                    Card Number
                  </label>
                  <input type="text" placeholder="1234 5678 9012 3456" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1479" />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-1480">
                  <div data-id="element-1481">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1482">
                      Expiry Date
                    </label>
                    <input type="text" placeholder="MM/YY" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1483" />
                  </div>
                  <div data-id="element-1484">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1485">
                      CVV
                    </label>
                    <input type="text" placeholder="123" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1486" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Order Summary - Sticky on Desktop, Normal on Mobile */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:sticky lg:top-24 lg:self-start" data-id="element-1487">
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-bone-dark" data-id="element-1488">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-4 sm:mb-6" data-id="element-1489">
                Order Summary
              </h2>

              {/* Product */}
              <div className="flex gap-3 sm:gap-4 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-bone-dark" data-id="element-1490">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-bone rounded-lg flex-shrink-0 overflow-hidden" data-id="element-1491">
                  <img src="https://cdn.magicpatterns.com/uploads/eTQ9DmqViHRFHehcQ8Qywk/GINEXT.png" alt="GINEXT Ginger Oil" className="w-full h-full object-cover" loading="lazy" data-id="element-1492" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1493">
                  <h3 className="font-bold text-charcoal text-sm sm:text-base mb-1" data-id="element-1494">
                    {selectedBundle.subtitle}
                  </h3>
                  <p className="text-xs sm:text-sm text-charcoal-light mb-2" data-id="element-1495">
                    15ml per Bottle
                  </p>
                  <div className="flex items-center justify-between" data-id="element-1496">
                    <span className="text-xs sm:text-sm text-charcoal-light" data-id="element-1497">
                      Qty: {selectedBundle.bottles}
                    </span>
                    <span className="font-semibold text-charcoal text-sm sm:text-base" data-id="element-1498">
                      ${selectedBundle.price.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6" data-id="element-1499">
                <div className="flex justify-between text-sm sm:text-base text-charcoal-light" data-id="element-1500">
                  <span data-id="element-1501">Subtotal</span>
                  <span data-id="element-1502">${selectedBundle.price.toFixed(2)}</span>
                </div>
                {selectedBundle.savings > 0 && <div className="flex justify-between text-sm sm:text-base text-herbal font-medium" data-id="element-1503">
                    <span data-id="element-1504">Savings ({selectedBundle.savings}%)</span>
                    <span data-id="element-1505">
                      -$
                      {(selectedBundle.originalPrice - selectedBundle.price).toFixed(2)}
                    </span>
                  </div>}
                <div className="flex justify-between text-sm sm:text-base text-charcoal-light" data-id="element-1506">
                  <span data-id="element-1507">Shipping</span>
                  {freeShipping ? <span className="text-herbal font-medium" data-id="element-1508">Free</span> : <span data-id="element-1509">$4.99</span>}
                </div>
                <div className="pt-2 sm:pt-3 border-t border-bone-dark flex justify-between text-base sm:text-lg font-bold text-charcoal" data-id="element-1510">
                  <span data-id="element-1511">Total</span>
                  <span data-id="element-1512">
                    $
                    {(selectedBundle.price + (freeShipping ? 0 : 4.99)).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={handlePurchase} className="w-full bg-ginger text-white py-5 sm:py-6 rounded-lg font-bold text-base sm:text-lg shadow-warm hover:shadow-warm-hover transition-all duration-300 mb-4 sm:mb-6 cursor-pointer text-center" data-id="element-1513">
                <div className="mb-1" data-id="element-1514">Complete Purchase</div>
                <p className="text-xs sm:text-sm font-normal text-white/90" data-id="element-1515">
                  30 Days Money Back Guaranteed
                </p>
              </motion.div>

              {/* Trust Signals - Simplified on Mobile */}
              <div className="space-y-3 text-xs sm:text-sm text-charcoal-light border-t border-bone-dark pt-4" data-id="element-1516">
                <div className="flex items-center gap-2" data-id="element-1517">
                  <ShieldCheck className="w-4 h-4 text-herbal flex-shrink-0" data-id="element-1518" />
                  <span data-id="element-1519">30-Day Money Back Guarantee</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1520">
                  <Truck className="w-4 h-4 text-herbal flex-shrink-0" data-id="element-1521" />
                  <span data-id="element-1522">
                    {freeShipping ? 'Free Shipping on This Order' : 'Free Shipping on Orders Over $50'}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1523">
                  <CreditCard className="w-4 h-4 text-herbal flex-shrink-0" data-id="element-1524" />
                  <span data-id="element-1525">Secure Payment Processing</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}